import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tournament, Team } from '@/types';
import { getTournament, getMatchesByTournament, getTeams, getTournamentStats, discardMatch, calculateDuration, getTournamentDataById, getTournamentParticipants } from '@/lib/supabase';
import { exportTournamentData } from '@/lib/export';
import { TeamLogo } from '@/components/TeamLogo';
import { saveMatchRef } from '@/lib/persistence';
import { exportMatchById } from '@/lib/export';
import { useAuth } from '@/lib/auth';
import { getSetScoreColor } from '@/lib/setScoreColor';
import AppHeader from '@/components/AppHeader';
import TournamentStatsModal from '@/components/modals/TournamentStatsModal';
import ScheduleMatchModal, { ScheduledMatchData } from '@/components/modals/ScheduleMatchModal';
import MatchDetailsDrawer from '@/components/modals/MatchDetailsDrawer';

interface MatchWithTeams {
  id: string;
  matchNumber: number;
  broadcastId: string;
  date: string;
  matchTime: string | null;
  liveStreamUrl: string | null;
  matchType: 'league' | 'playoff';
  status: string;
  winnerId: string | null;
  teamA: Team | null;
  teamB: Team | null;
  winningPoint: number | null;
  goldenPoint: number | null;
  teamAId: string;
  teamBId: string;
  startedAt: string | null;
  endedAt: string | null;
  isLocked: boolean;
  sets: {
    id: string;
    set_number: number;
    score_a: number;
    score_b: number;
    winner_id: string | null;
    started_at: string | null;
    ended_at: string | null;
  }[];
}

interface TournamentDashboardProps {
  onSignOut: () => void;
}

type TabType = 'summary' | 'stats';

export default function TournamentDashboard({ onSignOut }: TournamentDashboardProps) {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const { isAdmin, isViewer, isScorer } = useAuth();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [matches, setMatches] = useState<MatchWithTeams[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [participatingTeamIds, setParticipatingTeamIds] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState({ total: 0, completed: 0, live: 0, pending: 0, scheduled: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState<MatchWithTeams | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingMatch, setEditingMatch] = useState<ScheduledMatchData | null>(null);
  const [exportingMatchId, setExportingMatchId] = useState<string | null>(null);
  const [exportingTournament, setExportingTournament] = useState(false);
  const [copiedBroadcastId, setCopiedBroadcastId] = useState<string | null>(null);

  const getPublicBaseUrl = () => {
    return import.meta.env.VITE_PUBLIC_BASE_URL || window.location.origin;
  };

  const getOverlayUrl = (broadcastId: string) => {
    return `${getPublicBaseUrl()}/overlay/${broadcastId}`;
  };

  const copyOverlayUrl = async (broadcastId: string) => {
    const url = getOverlayUrl(broadcastId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedBroadcastId(broadcastId);
      setTimeout(() => setCopiedBroadcastId(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  // Format teams data from DB (snake_case) to TypeScript type (camelCase)
  const formatTeams = (data: any[]): Team[] =>
    data.map(t => ({
      id: t.id,
      name: t.name,
      shortName: t.short_name,
      color: t.color,
      logoUrl: t.logo_url,
      players: (t.players || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        number: p.number,
        teamId: p.team_id,
      })),
    }));

  const mapMatches = (matchesData: any[], teamsData: Team[]): MatchWithTeams[] =>
    matchesData.map((m: any) => ({
      id: m.id,
      matchNumber: m.match_number,
      broadcastId: m.broadcast_id,
      date: m.date,
      matchTime: m.match_time,
      liveStreamUrl: m.live_stream_url ?? null,
      matchType: m.match_type,
      status: m.status,
      winnerId: m.winner_id,
      teamA: teamsData.find((t: Team) => t.id === m.team_a_id) || null,
      teamB: teamsData.find((t: Team) => t.id === m.team_b_id) || null,
      winningPoint: m.winning_point,
      goldenPoint: m.golden_point,
      teamAId: m.team_a_id,
      teamBId: m.team_b_id,
      startedAt: m.started_at,
      endedAt: m.ended_at,
      isLocked: m.is_locked ?? false,
      sets: m.sets || [],
    }));

  useEffect(() => {
    if (!tournamentId) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [tournamentData, matchesData, teamsData, statsData, participantIds] = await Promise.all([
          getTournament(tournamentId),
          getMatchesByTournament(tournamentId),
          getTeams(),
          getTournamentStats(tournamentId),
          getTournamentParticipants(tournamentId),
        ]);

        const formattedTeams = formatTeams(teamsData);
        setTournament(tournamentData);
        setStats(statsData);
        setAllTeams(formattedTeams);
        setParticipatingTeamIds(new Set(participantIds));
        setMatches(mapMatches(matchesData, formattedTeams));
      } catch (err) {
        console.error('Failed to load tournament:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tournamentId]);

  // Filter teams to only participating teams for match scheduling
  const teams = useMemo(() => {
    if (participatingTeamIds.size === 0) {
      return allTeams;
    }
    return allTeams.filter(t => participatingTeamIds.has(t.id));
  }, [allTeams, participatingTeamIds]);

  const refreshData = async () => {
    if (!tournamentId) return;
    try {
      const [matchesData, statsData] = await Promise.all([
        getMatchesByTournament(tournamentId),
        getTournamentStats(tournamentId),
      ]);
      setStats(statsData);
      setMatches(mapMatches(matchesData, allTeams));
    } catch (err) {
      console.error('Failed to refresh:', err);
    }
  };

  const scheduledMatches = useMemo(() => matches.filter(m => m.status === 'scheduled'), [matches]);
  const liveMatches = useMemo(() => matches.filter(m => m.status === 'live' || m.status === 'set-won' || m.status === 'first-server'), [matches]);
  const completedMatches = useMemo(() => matches.filter(m => m.status === 'match-won'), [matches]);

  const getStatusInfo = (status: string): { label: string; bgClass: string; textClass: string } => {
    switch (status) {
      case 'match-won':
        return { label: 'Completed', bgClass: 'bg-success/10', textClass: 'text-success' };
      case 'live':
      case 'set-won':
        return { label: 'Live', bgClass: 'bg-warning/10', textClass: 'text-warning' };
      case 'first-server':
        return { label: 'Starting', bgClass: 'bg-warning/10', textClass: 'text-warning' };
      case 'scheduled':
        return { label: 'Scheduled', bgClass: 'bg-info/10', textClass: 'text-info' };
      default:
        return { label: 'Pending', bgClass: 'bg-surface-raised', textClass: 'text-text-muted' };
    }
  };

  const isMatchLive = (status: string) => status === 'live' || status === 'set-won' || status === 'first-server';
  const isMatchCompleted = (status: string) => status === 'match-won';
  const isMatchScheduled = (status: string) => status === 'scheduled';

  const handleStartMatch = (match: MatchWithTeams) => {
    if (isViewer) return;

    // Do NOT saveMatchRef here — the match isn't started yet and writing to
    // localStorage now would overwrite any currently active match's ref,
    // causing the recovery screen to show the wrong match on the next load.
    // saveMatchRef is called by App.tsx's startMatch/startScheduledMatch after
    // the match is actually persisted to the DB.
    setSelectedMatchId(null);
    navigate('/', {
      state: {
        tournamentId,
        tournament,
        startScheduledMatchId: match.id,
        scheduledMatch: match,
      },
    });
  };

  const handleResumeMatch = (match: MatchWithTeams) => {
    setSelectedMatchId(null);
    navigate(`/match/${match.id}/resume`);
  };

  const handleViewMatch = (match: MatchWithTeams) => {
    setSelectedMatchId(null);
    navigate(`/match/${match.id}/view`);
  };

  const handleEditMatch = (match: MatchWithTeams) => {
    setSelectedMatchId(null);
    setEditingMatch({
      id: match.id,
      matchNumber: match.matchNumber,
      date: match.date,
      matchTime: match.matchTime,
      matchType: match.matchType,
      teamAId: match.teamAId,
      teamBId: match.teamBId,
      liveStreamUrl: match.liveStreamUrl,
    });
    setShowScheduleModal(true);
  };

  const handleScheduleMatch = () => {
    setEditingMatch(null);
    setShowScheduleModal(true);
  };

  const handleScheduleSuccess = async () => {
    setShowScheduleModal(false);
    setEditingMatch(null);
    await refreshData();
  };

  const handleExportMatch = async (matchId: string) => {
    setExportingMatchId(matchId);
    try {
      await exportMatchById(matchId);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExportingMatchId(null);
    }
  };

  const handleExportTournament = async () => {
    if (!tournamentId) return;
    setExportingTournament(true);
    try {
      const [tournamentMatches, teamsData] = await Promise.all([
        getTournamentDataById(tournamentId),
        getTeams(),
      ]);
      const formattedTeams = teamsData.map((t: any) => ({
        ...t,
        shortName: t.short_name,
        logoUrl: t.logo_url,
      }));
      const participatingIds = participatingTeamIds.size > 0 ? Array.from(participatingTeamIds) : undefined;
      exportTournamentData(tournamentMatches, formattedTeams, participatingIds);
    } catch (err) {
      console.error('Tournament export failed:', err);
    } finally {
      setExportingTournament(false);
    }
  };

  const handleDiscardMatch = async () => {
    if (!confirmDiscard) return;
    setDiscarding(true);
    try {
      await discardMatch(confirmDiscard.id);
      setSelectedMatchId(null);
      setConfirmDiscard(null);
      await refreshData();
    } catch (err) {
      console.error('Failed to discard match:', err);
    } finally {
      setDiscarding(false);
    }
  };

  const formatTime = (time: string | null) => time || '—';

  const formatDuration = (startedAt: string | null, endedAt: string | null): string => {
    const duration = calculateDuration(startedAt, endedAt);
    return duration || '—';
  };

  const currentMatchId = selectedMatchId;
  const currentMatch = currentMatchId ? matches.find(m => m.id === currentMatchId) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-text-secondary">Tournament not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={onSignOut}
        title={tournament.name}
        subtitle={`${new Date(tournament.date).toLocaleDateString()}${tournament.venue ? ` — ${tournament.venue}` : ''}`}
        onNavigate={() => navigate('/tournaments')}
        navIcon="back"
        tournamentId={tournamentId}
        tournamentName={tournament.name}
      />

      <div className="flex-1 overflow-y-auto safe-area-bottom">
        <div className="max-w-4xl mx-auto">
          {/* Tabs */}
          <div className="sticky top-0 bg-background border-b border-border z-10">
            <div className="flex">
              <button
                onClick={() => setActiveTab('summary')}
                className={`flex-1 py-3 px-4 text-sm font-semibold transition-colors border-b-2 ${
                  activeTab === 'summary'
                    ? 'text-primary border-primary'
                    : 'text-text-muted border-transparent hover:text-text-primary'
                }`}
              >
                Match Summary
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`flex-1 py-3 px-4 text-sm font-semibold transition-colors border-b-2 ${
                  activeTab === 'stats'
                    ? 'text-primary border-primary'
                    : 'text-text-muted border-transparent hover:text-text-primary'
                }`}
              >
                Tournament Statistics
              </button>
            </div>
          </div>

          {activeTab === 'summary' && (
            <div className="p-4 space-y-4">
              {/* Stats Summary */}
              <div className="grid grid-cols-4 gap-2">
                <div className="card text-center">
                  <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
                  <p className="text-xs text-text-muted">Total</p>
                </div>
                <div className="card text-center">
                  <p className="text-2xl font-bold text-info">{stats.scheduled ?? 0}</p>
                  <p className="text-xs text-text-muted">Scheduled</p>
                </div>
                <div className="card text-center">
                  <p className="text-2xl font-bold text-warning">{stats.live}</p>
                  <p className="text-xs text-text-muted">Live</p>
                </div>
                <div className="card text-center">
                  <p className="text-2xl font-bold text-success">{stats.completed}</p>
                  <p className="text-xs text-text-muted">Completed</p>
                </div>
              </div>

              {/* Participating Teams */}
              <div className="card bg-surface-raised">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                    Participating Teams ({teams.length})
                  </h3>
                </div>
                {teams.length === 0 ? (
                  <p className="text-sm text-text-muted">No teams registered for this tournament.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {teams.map(team => (
                      <div
                        key={team.id}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface border border-border"
                        style={{ borderLeftColor: team.color, borderLeftWidth: 3 }}
                      >
                        <TeamLogo team={team} size="xs" />
                        <span className="text-xs font-semibold" style={{ color: team.color }}>{team.shortName}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rules Summary */}
              <div className="card bg-surface-raised">
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Scoring Rules</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">League</p>
                    <p className="text-xs text-text-secondary">
                      Win: {tournament.leagueWinningPoint} | Golden: {tournament.leagueGoldenPoint}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Playoff</p>
                    <p className="text-xs text-text-secondary">
                      Win: {tournament.playoffWinningPoint} | Golden: {tournament.playoffGoldenPoint}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tournament Actions */}
              <div className="grid grid-cols-2 gap-2">
                {isAdmin && (
                  <button onClick={handleScheduleMatch} className="btn-primary bg-success text-white">
                    SCHEDULE MATCH
                  </button>
                )}
                {!isViewer && (
                  <button
                    onClick={handleExportTournament}
                    disabled={exportingTournament}
                    className={`${isAdmin ? '' : 'col-span-2'} btn-primary bg-primary text-white disabled:opacity-50`}
                  >
                    {exportingTournament ? 'Exporting...' : 'EXPORT TOURNAMENT'}
                  </button>
                )}
              </div>

              {/* Match Summary Table */}
              {matches.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-text-secondary">No matches scheduled yet</p>
                  <div className="flex gap-2 justify-center mt-4">
                    {isAdmin && (
                      <button onClick={handleScheduleMatch} className="btn-primary bg-success text-white">
                        SCHEDULE FIRST MATCH
                      </button>
                    )}
                    {!isViewer && (
                      <button
                        onClick={handleExportTournament}
                        disabled={exportingTournament}
                        className="btn-primary bg-primary text-white disabled:opacity-50"
                      >
                        {exportingTournament ? 'Exporting...' : 'EXPORT'}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Table Header */}
                  <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                    <div className="col-span-1">#</div>
                    <div className="col-span-2">Team A</div>
                    <div className="col-span-2">Team B</div>
                    <div className="col-span-2">Date / Time</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-1">Score</div>
                    <div className="col-span-1">Duration</div>
                    <div className="col-span-2">Actions</div>
                  </div>

                  {/* Match Rows */}
                  {[...liveMatches, ...scheduledMatches, ...completedMatches].map(match => {
                    const statusInfo = getStatusInfo(match.status);
                    const winner = match.winnerId ? (match.winnerId === match.teamAId ? match.teamA : match.teamB) : null;

                    return (
                      <div
                        key={match.id}
                        className="card cursor-pointer hover:border-primary/50 transition-colors"
                        onClick={() => setSelectedMatchId(match.id)}
                      >
                        {/* Mobile Layout */}
                        <div className="md:hidden space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-text-muted">Match #{match.matchNumber}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); copyOverlayUrl(match.broadcastId); }}
                                className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                              >
                                <span>{match.broadcastId}</span>
                                {copiedBroadcastId === match.broadcastId ? (
                                  <svg className="w-3.5 h-3.5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </button>
                            </div>
                            <span className={`px-2 py-0.5 text-xs font-bold rounded ${statusInfo.bgClass} ${statusInfo.textClass}`}>
                              {statusInfo.label}
                            </span>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {match.teamA && (
                                <div className="flex items-center gap-1.5">
                                  <TeamLogo team={match.teamA} size="xs" />
                                  <span className="text-sm font-medium" style={{ color: match.teamA.color }}>{match.teamA.shortName}</span>
                                </div>
                              )}
                              <span className="text-text-muted text-xs">vs</span>
                              {match.teamB && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-medium" style={{ color: match.teamB.color }}>{match.teamB.shortName}</span>
                                  <TeamLogo team={match.teamB} size="xs" />
                                </div>
                              )}
                            </div>
                            <span className="text-xs text-text-muted capitalize">{match.matchType}</span>
                          </div>

                          <div className="flex items-center justify-between text-xs text-text-muted">
                            <span>{new Date(match.date).toLocaleDateString()}</span>
                            <span>{match.matchTime || '—'}</span>
                          </div>

                          {/* Set Scores - Only completed sets, or In Progress indicator */}
                          {(() => {
                            const completedSets = (match.sets || []).filter(s => s.winner_id);
                            if (isMatchLive(match.status) && completedSets.length === 0) {
                              return <p className="text-xs text-warning font-medium">In Progress</p>;
                            }
                            if (completedSets.length === 0) return null;
                            return (
                              <div className="flex items-center gap-2 text-xs">
                                {completedSets.map(set => {
                                  const teamA = teams.find(t => t.id === match.teamAId);
                                  const teamB = teams.find(t => t.id === match.teamBId);
                                  const { color } = getSetScoreColor(set, teamA, teamB);
                                  return (
                                    <span
                                      key={set.id}
                                      className="px-1.5 py-0.5 rounded font-semibold"
                                      style={color ? { backgroundColor: `${color}20`, color } : undefined}
                                    >
                                      {set.score_a}-{set.score_b}
                                    </span>
                                  );
                                })}
                              </div>
                            );
                          })()}

                          {isMatchCompleted(match.status) && winner && (
                            <p className="text-xs text-success font-medium">Winner: {winner.shortName}</p>
                          )}

                          {isMatchCompleted(match.status) && match.startedAt && match.endedAt && (
                            <p className="text-xs text-text-muted">
                              Duration: {formatDuration(match.startedAt, match.endedAt)}
                            </p>
                          )}

                          {/* Action buttons */}
                          <div className="flex gap-2 pt-2 border-t border-border" onClick={e => e.stopPropagation()}>
                            {isMatchScheduled(match.status) && (isAdmin || isScorer) && !isViewer && (
                              <button
                                onClick={() => handleStartMatch(match)}
                                className="flex-1 py-2 rounded-lg text-xs font-bold bg-success text-white"
                              >
                                Start
                              </button>
                            )}
                            {isMatchLive(match.status) && (isAdmin || isScorer) && !isViewer && (
                              <button
                                onClick={() => handleResumeMatch(match)}
                                className="flex-1 py-2 rounded-lg text-xs font-bold bg-warning text-white"
                              >
                                Resume
                              </button>
                            )}
                            {(isMatchLive(match.status) || isMatchCompleted(match.status)) && (
                              <button
                                onClick={() => handleResumeMatch(match)}
                                className="flex-1 py-2 rounded-lg text-xs font-semibold bg-surface-raised text-text-primary"
                              >
                                View
                              </button>
                            )}
                            {isMatchCompleted(match.status) && !isViewer && (
                              <button
                                onClick={() => handleExportMatch(match.id)}
                                disabled={exportingMatchId === match.id}
                                className="flex-1 py-2 rounded-lg text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
                              >
                                {exportingMatchId === match.id ? 'Exporting...' : 'Export'}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-1">
                            <span className="text-sm font-semibold text-text-muted">#{match.matchNumber}</span>
                          </div>
                          <div className="col-span-2">
                            {match.teamA && (
                              <div className="flex items-center gap-1.5">
                                <TeamLogo team={match.teamA} size="xs" />
                                <span className="text-sm font-medium" style={{ color: match.teamA.color }}>{match.teamA.shortName}</span>
                              </div>
                            )}
                          </div>
                          <div className="col-span-2">
                            {match.teamB && (
                              <div className="flex items-center gap-1.5">
                                <TeamLogo team={match.teamB} size="xs" />
                                <span className="text-sm font-medium" style={{ color: match.teamB.color }}>{match.teamB.shortName}</span>
                              </div>
                            )}
                          </div>
                          <div className="col-span-2">
                            <span className="text-xs text-text-muted">{new Date(match.date).toLocaleDateString()}</span>
                            {match.matchTime && <span className="text-xs text-text-muted ml-1.5">{formatTime(match.matchTime)}</span>}
                          </div>
                          <div className="col-span-1">
                            <span className={`px-2 py-0.5 text-xs font-bold rounded ${statusInfo.bgClass} ${statusInfo.textClass}`}>
                              {statusInfo.label}
                            </span>
                          </div>
                          <div className="col-span-1 text-xs font-mono flex items-center gap-1 flex-wrap">
                            {(() => {
                              if (isMatchScheduled(match.status)) return <span className="text-text-muted">Not Started</span>;
                              const sets = match.sets || [];
                              if (isMatchLive(match.status)) {
                                const completed = sets.filter(s => s.winner_id);
                                if (completed.length === 0) return <span className="text-text-muted">In Progress</span>;
                                return completed.map(set => {
                                  const teamA = teams.find(t => t.id === match.teamAId);
                                  const teamB = teams.find(t => t.id === match.teamBId);
                                  const { color } = getSetScoreColor(set, teamA, teamB);
                                  return (
                                    <span
                                      key={set.id}
                                      className="font-semibold"
                                      style={color ? { color } : undefined}
                                    >
                                      {set.score_a}-{set.score_b}
                                    </span>
                                  );
                                });
                              }
                              if (sets.length === 0) return <span className="text-text-muted">—</span>;
                              return sets.map((set, i) => {
                                const teamA = teams.find(t => t.id === match.teamAId);
                                const teamB = teams.find(t => t.id === match.teamBId);
                                const { color } = getSetScoreColor(set, teamA, teamB);
                                return (
                                  <span key={set.id || i}>
                                    {i > 0 && <span className="text-text-muted mx-0.5">|</span>}
                                    <span className="font-semibold" style={color ? { color } : undefined}>
                                      {set.score_a}-{set.score_b}
                                    </span>
                                  </span>
                                );
                              });
                            })()}
                          </div>
                          <div className="col-span-1 text-xs text-text-muted">
                            {isMatchCompleted(match.status) ? formatDuration(match.startedAt, match.endedAt) : '—'}
                          </div>
                          <div className="col-span-2 flex flex-wrap gap-1" onClick={e => e.stopPropagation()}>
                            {isMatchScheduled(match.status) && (isAdmin || isScorer) && !isViewer && (
                              <button
                                onClick={() => handleStartMatch(match)}
                                className="px-2 py-1 rounded text-xs font-bold bg-success text-white"
                              >
                                Start
                              </button>
                            )}
                            {isMatchLive(match.status) && (isAdmin || isScorer) && !isViewer && (
                              <button
                                onClick={() => handleResumeMatch(match)}
                                className="px-2 py-1 rounded text-xs font-bold bg-warning text-white"
                              >
                                Resume
                              </button>
                            )}
                            {(isMatchLive(match.status) || isMatchCompleted(match.status)) && (
                              <button
                                onClick={() => handleResumeMatch(match)}
                                className="px-2 py-1 rounded text-xs font-semibold bg-surface-raised text-text-primary"
                              >
                                View
                              </button>
                            )}
                            {isMatchCompleted(match.status) && !isViewer && (
                              <button
                                onClick={() => handleExportMatch(match.id)}
                                disabled={exportingMatchId === match.id}
                                className="px-2 py-1 rounded text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
                              >
                                {exportingMatchId === match.id ? '...' : 'Export'}
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); copyOverlayUrl(match.broadcastId); }}
                              className="px-2 py-1 rounded text-xs font-semibold bg-surface-raised text-text-muted hover:text-primary flex items-center gap-1"
                              title={`Copy overlay URL (${match.broadcastId})`}
                            >
                              {copiedBroadcastId === match.broadcastId ? (
                                <svg className="w-3 h-3 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              ) : (
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'stats' && tournamentId && (
            <div className="p-4">
              <TournamentStatsModal
                tournamentId={tournamentId}
                tournamentName={tournament?.name}
                onClose={() => setActiveTab('summary')}
                embedded
              />
            </div>
          )}
        </div>
      </div>

      {/* Match Details Drawer */}
      {selectedMatchId && currentMatch && (
        <MatchDetailsDrawer
          matchId={selectedMatchId}
          tournamentName={tournament?.name}
          onClose={() => setSelectedMatchId(null)}
          onEdit={() => handleEditMatch(currentMatch)}
          onDiscard={() => setConfirmDiscard(currentMatch)}
          onStart={() => handleStartMatch(currentMatch)}
          onResume={() => handleResumeMatch(currentMatch)}
          onView={() => handleViewMatch(currentMatch)}
          isAdmin={isAdmin}
          isScorer={isScorer}
          isViewer={isViewer}
          onLockChange={refreshData}
        />
      )}

      {/* Discard Confirmation Dialog */}
      {confirmDiscard && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl p-6 max-w-sm w-full">
            <h2 className="text-lg font-bold text-text-primary mb-2">Discard Match?</h2>
            <p className="text-text-secondary text-sm mb-4">
              Are you sure you want to discard Match #{confirmDiscard.matchNumber}? This will delete all match data including sets and rallies. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDiscard(null)}
                disabled={discarding}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDiscardMatch}
                disabled={discarding}
                className="flex-1 bg-error text-white py-3 rounded-lg font-semibold disabled:opacity-50"
              >
                {discarding ? 'Discarding...' : 'Discard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Match Modal */}
      {showScheduleModal && tournamentId && (
        <ScheduleMatchModal
          tournamentId={tournamentId}
          teams={teams}
          editingMatch={editingMatch}
          onSuccess={handleScheduleSuccess}
          onCancel={() => { setShowScheduleModal(false); setEditingMatch(null); }}
        />
      )}
    </div>
  );
}
